import { useEffect, useRef, useState, type ReactNode } from 'react'
import Icon from '../../docs/Icon'

export type ToastMessage = {
  /** A stable key for the message, used to dismiss it. */
  id: string
  /** One short sentence about something that just happened. */
  message: ReactNode
  /** Success for a completed action; info for anything else. */
  tone?: 'info' | 'success'
}

export type ToastProps = {
  /** The messages on screen, oldest first. The region renders even when empty, so screen readers hear each new one. */
  toasts: ToastMessage[]
  /** Removes a message: called by its close button and when its time runs out. */
  onDismiss: (id: string) => void
  /** Milliseconds each message stays; 0 keeps it until it is closed. */
  duration?: number
  /** The accessible name of the region. */
  label?: string
}

/**
 * Brief status messages stacked at the bottom of the viewport, in a polite
 * live region. Each one closes itself after the duration, and the countdown
 * pauses while the pointer is over the message or focus is inside it. Errors
 * that need action belong in an alert or a field error, never a toast.
 */
export default function Toast({ toasts, onDismiss, duration = 6000, label = 'Notifications' }: ToastProps) {
  return (
    <section className="mds-toast-region" aria-label={label}>
      {/* The live region wraps the list: a list cannot itself take the status role. */}
      <div role="status" aria-live="polite" aria-atomic="false" aria-relevant="additions">
        <ol className="mds-toast-region__list">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} duration={duration} onDismiss={onDismiss} />
          ))}
        </ol>
      </div>
    </section>
  )
}

function ToastItem({ toast, duration, onDismiss }: { toast: ToastMessage; duration: number; onDismiss: (id: string) => void }) {
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const remaining = useRef(duration)
  const paused = hovered || focused

  // Counts down only while nothing holds the message open, and keeps the time
  // left across pauses so a long hover never restarts the full duration.
  useEffect(() => {
    if (duration <= 0 || paused) return
    const started = Date.now()
    const timer = window.setTimeout(() => onDismiss(toast.id), remaining.current)
    return () => {
      window.clearTimeout(timer)
      remaining.current = Math.max(0, remaining.current - (Date.now() - started))
    }
  }, [duration, paused, onDismiss, toast.id])

  const tone = toast.tone ?? 'info'
  return (
    <li
      className={`mds-toast mds-toast--${tone}`}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false)
      }}
    >
      <Icon name={tone === 'success' ? 'check' : 'info'} size={18} className="mds-toast__icon" />
      <p className="mds-toast__message">{toast.message}</p>
      <button type="button" className="mds-icon-button mds-toast__close" aria-label="Dismiss notification" onClick={() => onDismiss(toast.id)}>
        <Icon name="close" size={18} />
      </button>
    </li>
  )
}
